/*
 * Commissions: one row per client payment that earned the partner a share
 * (GET /commissions?from=&to=). Ranges are calendar dates in India; the
 * financial year runs April to March.
 */

import { useMemo, useState } from "react";
import { commissions, wallet, type Commission } from "../api/client";
import { useLoad } from "../api/useLoad";
import { useMe } from "../auth/AuthContext";
import { COMMISSION_STATE, EmptyState, LoadError, SkeletonRows, Status } from "./bits";
import { fmtDate, inr, ordinal, plural, programStatus, RANGES, rangeFor, rangeLabel, rateLabel, type RangeKey } from "./format";
import CommissionSheet from "./CommissionSheet";
import { CalendarRange, Receipt } from "./icons";
import TierCard from "./TierCard";
import "./clients.css";
import "./commissions.css";

export default function CommissionsWorkspace() {
  const me = useMe();
  const [rangeKey, setRangeKey] = useState<RangeKey>("fy");
  const range = useMemo(() => rangeFor(rangeKey), [rangeKey]);
  const list = useLoad(() => commissions.list(range), [range.from, range.to]);
  const rows = list.data?.items ?? [];
  const totals = list.data?.totals;
  const program = me.commission_program;
  const standing = program ? programStatus(program) : null;
  // The tier card shows the lifetime in-progress total, so it must not follow the range filter.
  const held = useLoad(() => (program ? wallet.get() : Promise.resolve(null)), [!!program]);
  const [open, setOpen] = useState<Commission | null>(null);

  return (
    <>
      <section className="page-heading">
        <div>
          <p className="eyebrow">Commissions</p>
          <h1>What you&apos;ve earned</h1>
          <p className="page-heading__sub">
            {program && standing ? (
              <>
                You earn a share of each referred client&apos;s subscription fee, excluding GST, at the rate for how
                many clients you have.{" "}
                {standing.active
                  ? `Commission becomes withdrawable ${me.hold_days} days after the client pays.`
                  : `Until your ${ordinal(program.activation_clients + 1)} client joins, commission is recorded as In progress and isn't payable yet.`}
              </>
            ) : me.commission_rate ? (
              <>
                You earn <strong>{rateLabel(me.commission_rate)}</strong> of each referred client&apos;s subscription
                fee, excluding GST. Commission becomes withdrawable {me.hold_days} days after the client pays.
              </>
            ) : (
              <>
                Your commission rate hasn&apos;t been set yet. You earn a share of each referred client&apos;s
                subscription fee, excluding GST; it becomes withdrawable {me.hold_days} days after the client pays.
              </>
            )}
          </p>
        </div>
      </section>

      <TierCard program={program} inProgress={held.data?.in_progress} />

      <div className="toolbar">
        <div className="range-tabs" role="group" aria-label="Date range">
          {RANGES.map((key) => (
            <button key={key} aria-pressed={rangeKey === key} onClick={() => setRangeKey(key)}>
              {rangeLabel(key)}
            </button>
          ))}
        </div>
        <span className="toolbar__count com-span">
          <CalendarRange size={14} />
          {range.from ? `${fmtDate(range.from)} – ${fmtDate(range.to)}` : "All time"}
          {list.data ? ` · ${plural(rows.length, "payment")}` : ""}
        </span>
      </div>

      <section className={`stats${program ? " stats--5" : ""}`} aria-label="Totals for this range">
        <Tile label="Total" value={totals?.total} sub="Commission in this range" />
        <Tile label="Available" value={totals?.available} sub="Past the hold, withdrawable" tone="is-green" />
        <Tile label="Pending" value={totals?.pending} sub={`Inside the ${me.hold_days}-day hold`} tone="is-amber" />
        {program && (
          <Tile
            label="In progress"
            value={totals?.in_progress}
            sub={`Payable from your ${ordinal(program.activation_clients + 1)} client`}
            tone="is-gold"
          />
        )}
        <Tile label="Reversed" value={totals?.reversed} sub="After a refund, not paid" tone={Number(totals?.reversed) > 0 ? "is-red" : ""} />
      </section>

      <section className="card table-card" aria-label="Commissions" aria-busy={list.loading}>
        {list.error ? (
          <div className="table-card__state">
            <LoadError message={list.error} onRetry={list.reload} />
          </div>
        ) : !list.data ? (
          <SkeletonRows rows={5} label="Loading commissions" />
        ) : rows.length === 0 ? (
          <EmptyState icon={Receipt} title={rangeKey === "all" ? "No commission yet" : "No commission in this range"}>
            {rangeKey === "all"
              ? "Each time a client you referred pays their subscription, your share appears here."
              : "Try a longer range, or All."}
          </EmptyState>
        ) : (
          <div className="table-scroll">
            <table className="data-table is-stacked com-table">
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Client</th>
                  <th scope="col">Invoice</th>
                  <th scope="col" className="r">Paid (incl. GST)</th>
                  <th scope="col" className="r">Fee (excl. GST)</th>
                  <th scope="col" className="r">Rate</th>
                  <th scope="col" className="r">Commission</th>
                  <th scope="col">State</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <CommissionRow
                    key={c.id}
                    c={c}
                    activationClient={program ? program.activation_clients + 1 : null}
                    onOpen={() => setOpen(c)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {open && <CommissionSheet c={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function Tile({ label, value, sub, tone = "" }: { label: string; value: string | undefined; sub: string; tone?: string }) {
  return (
    <article className="card stat">
      <p className="eyebrow">{label}</p>
      {value !== undefined ? (
        <strong className={tone}>{inr(value)}</strong>
      ) : (
        <span className="skel skel--lg" style={{ marginTop: 10, width: "60%" }} />
      )}
      <small>{sub}</small>
    </article>
  );
}

function CommissionRow({
  c,
  activationClient,
  onOpen,
}: {
  c: Commission;
  activationClient: number | null;
  onOpen: () => void;
}) {
  const st = COMMISSION_STATE[c.state];
  return (
    // The row is clickable for mouse users; the client button is the keyboard and screen-reader way in.
    <tr className={`client-row${c.state === "reversed" ? " is-reversed" : ""}`} onClick={onOpen}>
      <td data-label="Date" className="com-date">{fmtDate(c.earned_at)}</td>
      <td className="is-title com-client">
        <button type="button" className="client-open" onClick={onOpen} aria-haspopup="dialog">
          {c.client_name ?? "—"}
        </button>
        {c.plan_name && <small className="com-plan">{c.plan_name} plan</small>}
      </td>
      <td data-label="Invoice" className="com-invoice">{c.invoice_number ?? "—"}</td>
      <td data-label="Paid (incl. GST)" className="r">{inr(c.payment_amount)}</td>
      <td data-label="Fee (excl. GST)" className="r">{inr(c.base_amount)}</td>
      <td data-label="Rate" className="r">{rateLabel(c.rate)}</td>
      <td data-label="Commission" className="r">
        <span className={c.state === "reversed" ? "is-struck" : "cell-strong"}>{inr(c.amount)}</span>
      </td>
      <td data-label="State">
        <span className="com-state">
          <Status tone={st.tone}>
            {c.state === "pending" ? `Pending until ${fmtDate(c.available_at)}` : st.label}
          </Status>
          {c.state === "in_progress" && activationClient !== null && (
            <small>Payable once your {ordinal(activationClient)} client joins</small>
          )}
          {c.state === "reversed" && c.reversed_reason && <small>{c.reversed_reason}</small>}
          {c.state === "available" && <small>Since {fmtDate(c.available_at)}</small>}
        </span>
      </td>
    </tr>
  );
}
